export const MAX_DOCUMENT_PARAGRAPHS = 2_000;
export const MAX_WORD_MATRIX_CELLS = 120_000;

export type WordChange = { kind: 'equal' | 'add' | 'remove'; text: string };
export type ParagraphChange = {
  kind: 'equal' | 'add' | 'remove' | 'change';
  before: string;
  after: string;
  words: WordChange[];
  addedWords?: number;
  removedWords?: number;
};

export type DocumentDiff = {
  rows: ParagraphChange[];
  changedParagraphs: number;
  addedWords: number;
  removedWords: number;
  truncated: boolean;
};

function paragraphs(text: string): string[] {
  const value = text.replaceAll('\r\n', '\n').trim();
  return value ? value.split(/\n\s*\n/u) : [];
}

function tokens(text: string): string[] {
  // Keep trailing whitespace with its word so the LCS does not spend its
  // budget aligning spaces before it reaches the stable suffix.
  return text.match(/\S+\s*/gu) ?? [];
}

// Every character belongs to a token, leading whitespace included, so the
// tokens of a text join back to exactly that text.
function exactTokens(text: string): string[] {
  return text.match(/\s+|\S+\s*/gu) ?? [];
}

function wordDiff(before: string, after: string, split = tokens): WordChange[] {
  const left = split(before);
  const right = split(after);
  if (left.length * right.length > MAX_WORD_MATRIX_CELLS) {
    return [
      ...(left.length ? [{ kind: 'remove' as const, text: before }] : []),
      ...(right.length ? [{ kind: 'add' as const, text: after }] : []),
    ];
  }
  const table = Array.from({ length: left.length + 1 }, () => new Uint32Array(right.length + 1));
  for (let i = left.length - 1; i >= 0; i -= 1)
    for (let j = right.length - 1; j >= 0; j -= 1)
      table[i][j] =
        left[i] === right[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
  const result: WordChange[] = [];
  let i = 0;
  let j = 0;
  while (i < left.length || j < right.length) {
    if (i < left.length && j < right.length && left[i] === right[j]) {
      result.push({ kind: 'equal', text: left[i++] });
      j += 1;
    } else if (j < right.length && (i === left.length || table[i][j + 1] >= table[i + 1][j])) {
      result.push({ kind: 'add', text: right[j++] });
    } else {
      result.push({ kind: 'remove', text: left[i++] });
    }
  }
  return result;
}

/**
 * A word diff that loses no character: the removed and equal words join back
 * to `before`, the added and equal words to `after`. Within each run of
 * changed words, removals come before additions (the "Suggesting" reading
 * order).
 */
export function exactWordDiff(before: string, after: string): WordChange[] {
  const words = wordDiff(before, after, exactTokens);
  const result: WordChange[] = [];
  let removed = '';
  let added = '';
  const flush = () => {
    if (removed) result.push({ kind: 'remove', text: removed });
    if (added) result.push({ kind: 'add', text: added });
    removed = '';
    added = '';
  };
  for (const word of words) {
    if (word.kind === 'remove') removed += word.text;
    else if (word.kind === 'add') added += word.text;
    else {
      flush();
      const last = result[result.length - 1];
      if (last?.kind === 'equal') last.text += word.text;
      else result.push({ kind: 'equal', text: word.text });
    }
  }
  flush();
  return result;
}

function paragraphPairs(
  before: string[],
  after: string[],
): Array<[string | undefined, string | undefined]> {
  const table = Array.from({ length: before.length + 1 }, () => new Uint32Array(after.length + 1));
  for (let i = before.length - 1; i >= 0; i -= 1)
    for (let j = after.length - 1; j >= 0; j -= 1)
      table[i][j] =
        before[i] === after[j]
          ? table[i + 1][j + 1] + 1
          : Math.max(table[i + 1][j], table[i][j + 1]);
  const pairs: Array<[string | undefined, string | undefined]> = [];
  let i = 0;
  let j = 0;
  while (i < before.length || j < after.length) {
    if (i < before.length && j < after.length && before[i] === after[j]) {
      pairs.push([before[i++], after[j++]]);
    } else if (j < after.length && (i === before.length || table[i][j + 1] >= table[i + 1][j])) {
      pairs.push([undefined, after[j++]]);
    } else if (i < before.length) {
      pairs.push([before[i++], undefined]);
    } else {
      pairs.push([undefined, after[j++]]);
    }
  }
  const merged: Array<[string | undefined, string | undefined]> = [];
  for (let index = 0; index < pairs.length; index += 1) {
    const current = pairs[index];
    const next = pairs[index + 1];
    if (!current) continue;
    if (next && current[1] === undefined && next[0] === undefined) {
      merged.push([current[0], next[1]]);
      index += 1;
    } else if (next && current[0] === undefined && next[1] === undefined) {
      merged.push([next[0], current[1]]);
      index += 1;
    } else {
      merged.push(current);
    }
  }
  return merged;
}

export function diffDocuments(beforeText: string, afterText: string): DocumentDiff {
  const before = paragraphs(beforeText);
  const after = paragraphs(afterText);
  const truncated =
    before.length > MAX_DOCUMENT_PARAGRAPHS || after.length > MAX_DOCUMENT_PARAGRAPHS;
  const pairs = paragraphPairs(
    before.slice(0, MAX_DOCUMENT_PARAGRAPHS),
    after.slice(0, MAX_DOCUMENT_PARAGRAPHS),
  );
  const rows = pairs.map(([left, right]): ParagraphChange => {
    if (left === undefined)
      return {
        kind: 'add',
        before: '',
        after: right ?? '',
        words: wordDiff('', right ?? ''),
        addedWords: tokens(right ?? '').length,
      };
    if (right === undefined)
      return {
        kind: 'remove',
        before: left,
        after: '',
        words: wordDiff(left, ''),
        removedWords: tokens(left).length,
      };
    if (left === right)
      return { kind: 'equal', before: left, after: right, words: [{ kind: 'equal', text: left }] };
    const words = wordDiff(left, right);
    const bounded = tokens(left).length * tokens(right).length > MAX_WORD_MATRIX_CELLS;
    return bounded
      ? {
          kind: 'change',
          before: left,
          after: right,
          words,
          addedWords: tokens(right).length,
          removedWords: tokens(left).length,
        }
      : { kind: 'change', before: left, after: right, words };
  });
  const addedWords = rows.reduce(
    (sum, row) => sum + (row.addedWords ?? row.words.filter((word) => word.kind === 'add').length),
    0,
  );
  const removedWords = rows.reduce(
    (sum, row) =>
      sum + (row.removedWords ?? row.words.filter((word) => word.kind === 'remove').length),
    0,
  );
  return {
    rows,
    changedParagraphs: rows.filter((row) => row.kind !== 'equal').length,
    addedWords,
    removedWords,
    truncated,
  };
}
